import { Loader2, Lock } from "lucide-react";
import { Button, cn } from "@eduviaos/ui-kit";
import type { CourseLeaf } from "../../../lib/learning/types";
import { useCanvasLearnStrings } from "../../../lib/i18n/learnLocaleBridge";
import type { RoadmapStep } from "./types";

/**
 * The readable half of a checkpoint. The marker itself is a 3D object in the
 * scene; this card is DOM anchored to it, so text stays crisp at every depth
 * instead of being rasterised into the WebGL canvas.
 */
export function CheckpointCard({
  step,
  checkpointNumber,
  isFocus,
  busy,
  onAction,
}: {
  step: RoadmapStep;
  checkpointNumber: number;
  isFocus: boolean;
  busy?: boolean;
  onAction?: (step: RoadmapStep) => void;
}) {
  const s = useCanvasLearnStrings();
  const done = step.status === "completed";
  const locked = step.locked || step.status === "locked";
  const leaf = step.meta?.leaf as CourseLeaf | undefined;

  if (step.kind === "phase") {
    const doneCount = step.phaseDone ?? 0;
    const total = step.phaseTotal ?? 0;
    const pct = total ? Math.round((doneCount / total) * 100) : 0;
    return (
      <div className="w-max max-w-[13rem] rounded-xl border border-amber-600/40 bg-amber-50 px-3 py-1.5 text-center shadow-lg">
        <p className="font-display text-[0.6rem] font-bold uppercase tracking-[0.12em] text-amber-800">
          {s.phaseLabel}
        </p>
        <p className="text-[0.78rem] font-semibold leading-snug text-amber-950">{step.title}</p>
        <p className="text-[0.62rem] tabular-nums text-amber-800/90">
          {doneCount}/{total} · {pct}%
        </p>
      </div>
    );
  }

  return (
    <article
      className={cn(
        "border-border/70 bg-card w-[min(16rem,72vw)] rounded-xl border p-2.5 text-left shadow-2xl",
        done && "border-joy-green/50 bg-joy-green/[0.12]",
        isFocus && !done && !locked && "border-primary/50 ring-primary/25 ring-2",
        locked && "border-border/50 saturate-[0.6]",
      )}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground text-[0.58rem] font-bold uppercase tracking-[0.12em]">
          {s.checkpointNumbered(checkpointNumber)}
        </span>
        {locked && <Lock className="text-muted-foreground size-3" aria-hidden />}
      </div>
      <p className="mt-0.5 text-[0.82rem] font-semibold leading-snug">{step.title}</p>
      {leaf && (
        <p className="text-muted-foreground mt-0.5 text-[0.65rem]">
          {leaf.read_log_count > 0 ? s.readsLogged(leaf.read_log_count) : s.noReadsYet}
          {leaf.estimated_minutes ? ` · ${s.estimatedMinutes(leaf.estimated_minutes)}` : ""}
        </p>
      )}
      {leaf?.status === "available" && leaf.requires_step_quiz && onAction && (
        <Button
          size="sm"
          disabled={busy}
          className="mt-2 h-7 w-full rounded-full text-[0.65rem] font-semibold transition-transform duration-150 active:scale-[0.97]"
          onClick={() => onAction(step)}
        >
          {busy ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            s.qualifyWithQuestions(leaf.min_questions_required)
          )}
        </Button>
      )}
    </article>
  );
}
